import { useMemo } from 'react';
import { Form, Formik } from 'formik';
import { styled } from '~/themes';
import {
  GENERAL_SETTING_DEFAULT,
  TIMEZONE_SETTING,
} from '~/constants/adminOptions';
import Loading from '~/components/common/CirularLoading';
import { IAccount } from '~/models/adminOptions';
import { alertParams, showAlert } from '~/components/common/Alert';
import { useAuth } from '~/providers/AuthProvider';
import { useGetTimezoneQuery } from '~/services/api/userManagement';
import {
  useUpdateGeneralSettingsMutation,
  useGetCFNotificationSetting,
} from '~/services/api/shared';
import { errorAlert } from '~/components/common/Alert/alertParams';
import { isObjectEqual } from '~/utils/common';
import omit from 'lodash/omit';
import { ChangeAccountSchema } from '~/utils/schema/user';
import httpClient from '~/lib/http-client';
import { NOTIFICATION_ENDPOINTS } from '~/config';
import { queryClient } from '~/lib/react-query';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import ActionBar from '../components/ActionBar';
import { GeneralContent as GeneralContentV2 } from './components/GeneralContentV2';
import { GeneralContent } from './components/GeneralContent';

const FormContainer = styled(Form)(() => ({
  width: '100%',
  height: '100%',
}));

type IProps = {};

const General: React.FC<IProps> = () => {
  const isNotiV2 = useFeatureIsOn('cs_9035_notification');

  const { account, isFetchingAccounts } = useAuth();
  const { data: dataTimezone, isLoading: isLoadingTimezone } =
    useGetTimezoneQuery();

  const { data: cfNotificationSetting } = useGetCFNotificationSetting({
    params: account?.org_id,
    enabled: !!account?.org_id,
  });

  const { mutate: updateGeneralSettings, isLoading: loadingUpdate } =
    useUpdateGeneralSettingsMutation({
      onSuccess: () => {
        showAlert({
          ...alertParams.success,
          closeOnClick: true,
        });
        queryClient.invalidateQueries(['useGetCFNotificationSetting']);
        httpClient.json.post(
          'notifications/me/sync-settings',
          {
            metadata: {},
          },
          {
            baseURL: NOTIFICATION_ENDPOINTS,
          },
        );
      },
      onError: (err) => {
        showAlert(errorAlert(err));
      },
    });

  const initialValues = useMemo(() => {
    if (!account) return GENERAL_SETTING_DEFAULT;

    const mergedNotificationSetting = {
      ...account.notification_setting,
      ...(cfNotificationSetting?.data || {}),
    };

    return {
      ...account,
      timezoneType:
        account?.timezone === TIMEZONE_SETTING.AUTO_DETECT
          ? TIMEZONE_SETTING.AUTO_DETECT
          : TIMEZONE_SETTING.MANUALLY,
      notification_setting: {
        ...mergedNotificationSetting,
        repeatingInterval: String(
          mergedNotificationSetting?.repeatingInterval ?? 0,
        ),
      },
    };
  }, [account, cfNotificationSetting?.data]);

  const onSubmit = ({
    id,
    first_name: _,
    last_name: __,
    timezoneType,
    timezone,
    ...values
  }: IAccount) => {
    delete values.tooltip;
    updateGeneralSettings({
      body: {
        ...values,
        timezone:
          timezoneType === TIMEZONE_SETTING.AUTO_DETECT
            ? TIMEZONE_SETTING.AUTO_DETECT
            : timezone,
        notification_setting: {
          ...values.notification_setting,
          repeatingInterval: Number(
            values.notification_setting?.repeatingInterval ?? 0,
          ),
        },
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={ChangeAccountSchema}
    >
      {({ values, resetForm, submitForm, dirty }) => (
        <FormContainer>
          <Loading loading={isFetchingAccounts} />
          {isNotiV2 ? (
            <GeneralContentV2
              dataTimezone={dataTimezone}
              isLoadingTimezone={isLoadingTimezone}
            />
          ) : (
            <GeneralContent
              dataTimezone={dataTimezone}
              isLoadingTimezone={isLoadingTimezone}
            />
          )}

          {dirty &&
            !isObjectEqual(
              omit(values, ['avatar']),
              omit(account || GENERAL_SETTING_DEFAULT, ['avatar']),
            ) && (
              <ActionBar
                onSave={submitForm}
                onCancel={() => resetForm()}
                loading={loadingUpdate}
              />
            )}
        </FormContainer>
      )}
    </Formik>
  );
};

export default General;
